// DeviceVerificationModal — app-level trusted-device verification.
//
// Shown when the API returns 403 { code: 'DEVICE_NOT_TRUSTED' } (only happens
// when REQUIRE_TRUSTED_DEVICE is enabled server-side). Walks the user through
// an email OTP, stores the returned device token, and refetches data.

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useDeviceTrustStore } from '../store';
import { devicesApi } from '../lib/api';
import { setDeviceToken } from '../lib/deviceToken';

export default function DeviceVerificationModal() {
  const required = useDeviceTrustStore((s) => s.verificationRequired);
  const setRequired = useDeviceTrustStore((s) => s.setVerificationRequired);
  const queryClient = useQueryClient();
  const { logout } = useAuth0();

  const [step, setStep] = useState<'intro' | 'code'>('intro');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  if (!required) return null;

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await devicesApi.verifyStart();
      setStep('code');
      setInfo('We emailed a 6-digit code to your address. It expires in 10 minutes.');
    } catch (e: any) {
      setError(e?.message || 'Could not send the code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await devicesApi.verifyConfirm(code.trim());
      const token = res?.data?.device_token as string | undefined;
      if (!token) throw new Error('Verification failed. Please try again.');
      setDeviceToken(token);
      setRequired(false);
      setStep('intro');
      setCode('');
      setInfo(null);
      // Device is now trusted — refetch everything that may have 403'd.
      await queryClient.invalidateQueries();
    } catch (e: any) {
      setError(e?.message || 'Invalid code. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Verify this device</h2>
        <p className="mt-2 text-sm text-gray-600">
          For your patients&apos; security, this device must be verified before it can access patient data.
        </p>

        {info && <p className="mt-3 rounded bg-blue-50 px-3 py-2 text-sm text-blue-800">{info}</p>}
        {error && <p className="mt-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

        {step === 'intro' ? (
          <button
            onClick={sendCode}
            disabled={busy}
            className="mt-5 w-full rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Email me a verification code'}
          </button>
        ) : (
          <>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
              inputMode="numeric"
              autoFocus
              placeholder="123456"
              aria-label="6-digit verification code"
              className="mt-5 w-full rounded-md border border-gray-300 px-3 py-2 text-center text-lg tracking-[0.5em]"
            />
            <button
              onClick={verify}
              disabled={busy || code.length !== 6}
              className="mt-3 w-full rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? 'Verifying…' : 'Verify device'}
            </button>
            <button
              onClick={sendCode}
              disabled={busy}
              className="mt-2 w-full text-sm text-blue-600 hover:underline disabled:opacity-50"
            >
              Resend code
            </button>
          </>
        )}

        <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
          <span>If you didn&apos;t expect this, contact your practice administrator.</span>
          <button
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="ml-3 shrink-0 hover:underline"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
